import { createConsumer } from '@rails/actioncable';
import { emitter } from 'shared/helpers/mitt';
import { AVISO_DE_MENSAGEM } from 'dashboard/routes/dashboard/internal-chat/eventos';
import logger from 'shared/helpers/logger';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { resumoDaMensagem } from 'dashboard/routes/dashboard/internal-chat/resumoDaMensagem';

export const INTERNAL_CHAT_EVENTS = {
  MESSAGE_CREATED: 'internal_chat.message_created',
  UNREAD_UPDATED: 'internal_chat.unread_updated',
  TYPING: 'internal_chat.typing',
  // Resposta de thread NÃO reusa MESSAGE_CREATED de propósito: quem escuta
  // aquele evento acrescenta a mensagem na coluna, e resposta de thread não
  // pode aparecer lá — ver `scope :raizes` no backend.
  THREAD_REPLY_CREATED: 'internal_chat.thread_reply_created',
};

// 'message.updated'/'message.deleted' já eram transmitidos pelo backend
// (InternalChatMessage#broadcast_update_to_cable/broadcast_delete_to_cable)
// mas NUNCA eram escutados aqui — editar/apagar só refletia em tempo real
// pra quem fez a ação (via commit local), outros participantes só viam
// depois de sair e voltar da conversa. Achado corrigindo reações (mesmo
// método onChatReceived).

class InternalChatCable {
  constructor() {
    this.consumer = null;
    this.chatSubscription = null;
    this.accountSubscription = null;
    this.store = null;
    this.currentChatId = null;
    this.currentAccountId = null;
    this.currentUserId = null;
    this.pubsubToken = null;
    this.isInitialized = false;
    this.jaConectouNesteChat = false;
  }

  /**
   * Called once from App.vue (or equivalent) after auth.
   * pubsub_token is available at store.getters['auth/getCurrentUser'].pubsub_token
   * (the currentUser object on auth state has this field from the Rails API response).
   */
  init(store, pubsubToken) {
    if (this.isInitialized) return this;

    this.store = store;
    this.pubsubToken = pubsubToken;
    this.currentAccountId = store.getters.getCurrentAccountId;
    this.currentUserId = store.getters.getCurrentUserID;

    const { websocketURL = '' } = window.chatwootConfig || {};
    const wsUrl = websocketURL ? `${websocketURL}/cable` : undefined;

    this.consumer = createConsumer(wsUrl);
    this.isInitialized = true;

    // Subscribe to the account-level feed for unread badges
    this.subscribeToAccountFeed();

    return this;
  }

  /**
   * Subscribe to a specific chat channel to receive new messages in real time.
   * Unsubscribes from any previously watched chat first.
   */
  subscribeToChat(chatId) {
    if (!this.consumer || !this.store) {
      logger.warn('[InternalChatCable] Not initialized. Call init() first.');
      return;
    }

    // IDEMPOTENTE. Três lugares pedem a inscrição da mesma conversa — a tela
    // que lista (InternalChatView), a que exibe (ChatView) e o watch do token.
    // Como os PARÂMETROS são idênticos, o ActionCable trata as três como o
    // MESMO identificador: desinscrever uma derruba o stream das outras no
    // servidor, e sobra uma inscrição zumbi — `connected` disparou, mas nada
    // mais chega.
    //
    // É a causa do "às vezes só recarregando": dependia da corrida entre o
    // unsubscribe de uma e a confirmação da outra. No log dava para ver
    // `Chat channel connected: 1` DUAS vezes por carga.
    //
    // Reproduzido em 2026-08-06: com a conversa aberta, mensagem criada por
    // outra pessoa não apareceu até recarregar.
    if (this.chatSubscription && this.currentChatId === chatId) return;

    if (this.chatSubscription) {
      this.unsubscribeFromChat();
    }

    this.currentChatId = chatId;
    // Distingue a PRIMEIRA conexão de uma reconexão. `connected` dispara nas
    // duas, e só a segunda precisa buscar o que se perdeu.
    this.jaConectouNesteChat = false;

    this.chatSubscription = this.consumer.subscriptions.create(
      {
        channel: 'InternalChatChannel',
        chat_id: chatId,
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
      },
      {
        connected: () => {
          logger.log('[InternalChatCable] Chat channel connected:', chatId);

          // RECONECTOU. Enquanto o cabo esteve fora, todo `message.created`
          // transmitido se perdeu — ActionCable não guarda nada para entregar
          // depois. Sem esta busca, a conversa fica com um buraco silencioso
          // até a pessoa recarregar a página, que é exatamente o sintoma
          // relatado pelo Victor em 2026-08-06 ("às vezes só atualizando a
          // página").
          //
          // Wi-Fi oscilando, notebook voltando do sono e deploy do servidor
          // derrubam a conexão o tempo todo — reconectar é rotina, não exceção.
          if (this.jaConectouNesteChat) {
            logger.log(
              '[InternalChatCable] Reconectou — buscando o que ficou para trás'
            );
            this.store.dispatch('internalChats/get', chatId).catch(() => {
              // Falhou a busca: a próxima reconexão tenta de novo. Alertar
              // aqui seria barulho por algo que se resolve sozinho.
            });
          }
          this.jaConectouNesteChat = true;
        },
        disconnected: () => {
          logger.log('[InternalChatCable] Chat channel disconnected:', chatId);
        },
        rejected: () => {
          logger.warn(
            '[InternalChatCable] Chat subscription rejected for chat:',
            chatId
          );
        },
        received: data => this.onChatReceived(data),
      }
    );
  }

  /**
   * Notify the chat stream that the current user is (or stopped) typing.
   * No-op if not subscribed to a chat.
   */
  sendTyping(isTyping) {
    if (!this.chatSubscription) return;
    try {
      this.chatSubscription.perform('typing', { typing: !!isTyping });
    } catch (e) {
      logger.warn('[InternalChatCable] sendTyping failed', e);
    }
  }

  /**
   * Unsubscribe from the current chat channel (called on chat change or logout).
   */
  unsubscribeFromChat() {
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
      this.chatSubscription = null;
      this.currentChatId = null;
      this.jaConectouNesteChat = false;
    }
  }

  /**
   * Subscribe to AccountInternalChatChannel for global unread badge updates.
   * This channel broadcasts whenever a message is sent in any chat where
   * the user is a participant (except messages sent by themselves).
   */
  subscribeToAccountFeed() {
    if (!this.consumer) return;

    this.accountSubscription = this.consumer.subscriptions.create(
      {
        channel: 'AccountInternalChatChannel',
        pubsub_token: this.pubsubToken,
        account_id: this.currentAccountId,
        user_id: this.currentUserId,
      },
      {
        connected: () => {
          logger.log('[InternalChatCable] Account feed connected');
        },
        rejected: () => {
          logger.warn('[InternalChatCable] Account feed subscription rejected');
        },
        received: data => this.onAccountFeedReceived(data),
      }
    );
  }

  onChatReceived(data) {
    if (!data || !data.event) return;

    if (data.event === 'message.created') {
      const { message } = data;
      if (!message || !this.currentChatId) return;

      this.store.commit('internalChats/ADD_MESSAGE', {
        chatId: this.currentChatId,
        message,
      });
      this.store.commit('internalChats/SET_LAST_MESSAGE', {
        chatId: this.currentChatId,
        lastMessage: resumoDaMensagem(message),
      });

      // Pedido do Victor: status enviada/entregue/vista. Esse handler só
      // roda pro chat que a aba tem aberto agora (é o canal por-chat, não o
      // de conta inteira) — então chegou mensagem nova de outra pessoa
      // enquanto eu já tô OLHANDO a conversa. Sem isso, "vista" só
      // atualizaria da próxima vez que eu saísse e voltasse (mark_read só
      // rodava ao entrar na conversa).
      if (message.sender?.id && message.sender.id !== this.currentUserId) {
        this.store.dispatch('internalChats/markRead', this.currentChatId);
      }

      emitter.emit(INTERNAL_CHAT_EVENTS.MESSAGE_CREATED, {
        chatId: this.currentChatId,
        message,
      });
      return;
    }

    if (data.event === 'thread.reply.created') {
      if (!this.currentChatId) return;

      emitter.emit(INTERNAL_CHAT_EVENTS.THREAD_REPLY_CREATED, {
        chatId: this.currentChatId,
        threadParentId: data.thread_parent_id,
        repliesCount: data.thread_replies_count,
        lastReplyAt: data.thread_last_reply_at,
        message: data.message,
      });
      return;
    }

    if (data.event === 'message.updated') {
      const { message } = data;
      if (!message || !this.currentChatId) return;
      this.store.commit('internalChats/PATCH_MESSAGE', {
        chatId: this.currentChatId,
        messageId: message.id,
        changes: {
          content: message.content,
          edited: true,
          edited_at: message.edited_at,
        },
      });
      return;
    }

    if (data.event === 'message.deleted') {
      const { message } = data;
      if (!message || !this.currentChatId) return;
      this.store.commit('internalChats/REMOVE_MESSAGE', {
        chatId: this.currentChatId,
        messageId: message.id,
      });
      return;
    }

    if (data.event === 'message.pinned') {
      const { message } = data;
      if (!message || !this.currentChatId) return;
      this.store.commit('internalChats/PATCH_MESSAGE', {
        chatId: this.currentChatId,
        messageId: message.id,
        changes: {
          pinned_at: message.pinned_at,
          pinned_by_id: message.pinned_by_id,
        },
      });
      return;
    }

    if (data.event === 'reaction.updated') {
      const { message_id: messageId, reactions } = data;
      if (!messageId || !this.currentChatId) return;
      this.store.commit('internalChats/SET_MESSAGE_REACTIONS', {
        chatId: this.currentChatId,
        messageId,
        reactions: reactions || [],
      });
      return;
    }

    if (data.event === 'read.updated') {
      // Pedido do Victor: status enviada/entregue/vista — quando o OUTRO
      // participante lê, quem mandou vê o check ficar azul na hora, sem
      // precisar sair e voltar da conversa.
      const { user_id: userId, last_read_at: lastReadAt } = data;
      if (!userId || userId === this.currentUserId || !this.currentChatId)
        return;
      this.store.commit('internalChats/SET_OTHER_USER_LAST_READ', {
        chatId: this.currentChatId,
        lastReadAt,
      });
      return;
    }

    if (data.event === 'typing.start' || data.event === 'typing.stop') {
      const { user } = data;
      // Ignore our own typing echo.
      if (!user || user.id === this.currentUserId) return;

      emitter.emit(INTERNAL_CHAT_EVENTS.TYPING, {
        chatId: this.currentChatId,
        user,
        typing: data.event === 'typing.start',
      });
    }
  }

  onAccountFeedReceived(data) {
    if (!data || !data.event) return;

    if (data.event === 'unread_increment') {
      const { chat_id: chatId } = data;
      if (!chatId) return;

      // A prévia da lista vem no mesmo evento (só para mensagem da coluna, não
      // resposta de discussão). Antes do `return` de quem está com a conversa
      // aberta: a lista mostra a última de TODAS as conversas.
      if (data.last_message) {
        this.store.commit('internalChats/SET_LAST_MESSAGE', {
          chatId,
          lastMessage: data.last_message,
        });
      }

      // O backend só manda esse evento pros OUTROS participantes (nunca pro
      // remetente, ver InternalChatMessage#broadcast_unread_increment) —
      // então quem recebe aqui nunca é quem enviou. Toca som (pedido do
      // Victor 2026-07-16) reusando o mesmo helper/config de tom do inbox
      // de cliente — silencia sozinho se a aba estiver em foco JÁ nessa
      // conversa (não precisa dessa checagem de novo aqui).
      const activeChat = this.store.getters['internalChats/getActiveChat'];
      const isViewingThisChat = !!(activeChat && activeChat.id === chatId);
      DashboardAudioNotificationHelper.notifyInternalChatMessage({
        isViewingThisChat,
      });

      // Only increment if this chat is not currently open
      if (isViewingThisChat) return;

      // AVISO ESTILO MSN. Só sobe se o backend mandou os dados — ele omite
      // `aviso` para quem silenciou a conversa, então o silêncio é resolvido
      // lá, sem o front precisar saber de mute.
      //
      // Depois do `return` acima de propósito: quem está com a conversa aberta
      // não recebe tarja, porque a mensagem já apareceu na tela.
      if (data.aviso) {
        emitter.emit(AVISO_DE_MENSAGEM, {
          chatId,
          remetente: data.aviso.remetente,
          previa: data.aviso.previa,
          temAnexo: data.aviso.tem_anexo,
          nomeDoChat: data.aviso.nome_do_chat,
          recebidoEm: Date.now(),
        });
      }

      this.store.commit('internalChats/INCREMENT_UNREAD', chatId);

      emitter.emit(INTERNAL_CHAT_EVENTS.UNREAD_UPDATED, { chatId });
    }
  }

  /**
   * Full cleanup — called on logout.
   */
  disconnect() {
    this.unsubscribeFromChat();

    if (this.accountSubscription) {
      this.accountSubscription.unsubscribe();
      this.accountSubscription = null;
    }

    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = null;
    }

    this.store = null;
    this.isInitialized = false;
  }
}

// Singleton — same pattern as pipelineCardCable
const internalChatCable = new InternalChatCable();

export default internalChatCable;
